import { genericGetRequestToApi } from '../../utils/genericRequests'
import { RetrieveProfileUrl } from '../../utils/agentUrls'
import {
  sortNames,
  isEmpty,
  sortObjectArrayByKey
} from '../../utils/generalUtils'
import {
  getAllMessages,
  markMessageAsRead,
  calculateThreadsFromMessages
} from '../../services/messagesService'
import { getPropertyById } from '../../services/propertiesService'
import { createCalendarTimeFrame } from '../../services/routeService'
import { setProfileLastUpdatedTimestamp } from '../../services/profile'

let interval

const getDefaultAgentState = () => {
  return {
    agentData: null,
    activeAgentData: null,
    agentDialogs: [],
    agentCalendarEvents: {},
    agentMessages: [],
    selectedDialogUser: null,
    selectedTab: 0,
    clientSavedHomes: [],
    agentRouteData: [],
    // Start/End dropdown values for Plan route page
    selectedStartLocation: {},
    selectedEndLocation: {},
    propertiesData: [],
    routeEditMode: false,
    routeCalculatedArray: [],
    rhtValue: {
      loading: true,
      data: []
    },
    hideMap: true,
    resetExpansionPanels: false,
    favorableViewTime: 20
  }
}

const state = getDefaultAgentState()

const getters = {
  getActiveAgentData: state => state['activeAgentData'],
  getSortedClients: state =>
    state.agentData ? sortNames(state.agentData.clients) : [],
  getAgentCalendarEvents: state => state['agentCalendarEvents'],
  agentUnreadMessages: state => {
    if (state.agentData && state.agentMessages) {
      return state.agentMessages.filter(
        message => !message.read && message.from !== state.agentData._id
      )
    }
  },
  getClient: state => id =>
    state.agentData.clients.find(client => client._id === id),
  getClients: state => state.agentData.clients,
  getAgentLogo: state =>
    state.agentData
      ? state.agentData.media.find(item => item.type === 'logo')
      : undefined
}

const actions = {
  unsubscribe() {
    if (interval) {
      clearInterval(interval)
    }
  },
  getDialogsUsers({ commit, state }) {
    if (state.agentData && state.agentMessages) {
      let dialogUsers = []
      state.agentMessages.forEach(message => {
        if (!dialogUsers.includes(message.from)) {
          dialogUsers.push(message.from)
        }
        if (!dialogUsers.includes(message.to[0])) {
          dialogUsers.push(message.to[0])
        }
      })
      dialogUsers = dialogUsers.filter(id => id !== state.agentData._id)
      dialogUsers = state.agentData && state.agentData.clients
        ? state.agentData.clients.map(sender => {
          const unreadMessages = state.agentMessages
            .filter(
              message => !message.read && message.from !== state.agentData._id
            )
            .filter(message => message.from === sender._id)
          return { ...sender, ...{ unreadMessages: unreadMessages } }
        })
        : []
      commit('SET_DIALOGS', dialogUsers)
    }
  },

  /**
   * Sets the selected client id depending on the entered search input by an agent
   *
   * @param {Object} state The current global state
   * @param {string} clientId it will consist of the selected client id
   */
  setSearchedClient({ dispatch, commit }, clientData) {
    commit('SET_RHT', { ...getDefaultAgentState().rhtValue })
    commit('client/SET_CLIENT_DATA', clientData, { root: true })
    commit('common/SET_ROUTE_HOMES', null, { root: true })
    dispatch(
      'common/getRouteData',
      ({ commit }, { data: clientData, type: 'client' }),
      { root: true }
    )
    const clientFavs = state.propertiesData.find(
      data => data.clientId === clientData._id
    )
    if (!isEmpty(clientFavs)) {
      commit('client/SAVE_CLIENT_FAVORITES', clientFavs.propertyData, {
        root: true
      })
    } else {
      commit('SET_RHT', { ...state.rhtValue, loading: false })
      dispatch('client/clientHomeDetailsAction', ({ commit }, clientData), {
        root: true
      })
    }
  },

  async loadRequestedHomeData(
    { dispatch, commit },
    { clientData, loadFavs = true }
  ) {
    commit('SET_RHT', { ...getDefaultAgentState().rhtValue })
    commit('client/SET_CLIENT_CHANGED', false, { root: true })
    const homeArray = clientData.homes.filter(
      home => home.requestTour === true
    )
    const rhtHomes = homeArray.sort((a, b) => {
      if (!a.requestTourTimestamp) return 1
      if (!b.requestTourTimestamp) return -1
      return (
        new Date(b.requestTourTimestamp) - new Date(a.requestTourTimestamp)
      )
    })
    commit('client/SET_CLIENT_DATA', clientData, { root: true })
    commit('common/SET_ROUTE_HOMES', null, { root: true })
    dispatch(
      'common/getRouteData',
      ({ commit }, { data: clientData, type: 'client' }),
      { root: true }
    )

    const extendedHomes = await Promise.all(
      rhtHomes.map(item => {
        return getPropertyById(item.propertyId)
      })
    )

    const clientHomesWithProperties = [...rhtHomes].map(home => {
      const homeProperty = extendedHomes.find(
        mls => mls.data._id === home.propertyId
      )
      home = {
        ...homeProperty.data,
        ...home,
        favoriteId: home._id,
        _id: homeProperty.data._id
      }
      return home
    })
    commit('SET_RHT', { loading: false, data: clientHomesWithProperties })
    commit('SET_PROPERTIES_DATA', {
      clientId: clientData._id,
      propertyData: clientHomesWithProperties
    })
    commit('client/SAVE_CLIENT_FAVORITES', clientHomesWithProperties, {
      root: true
    })

    if (loadFavs) {
      dispatch(
        'client/clientHomeDetailsAction',
        ({ commit }, { ...clientData, partial: true }),
        { root: true }
      )
    }
  },

  async updatePropertyStates(
    { commit, rootState },
    { updatedRhtHomes, updatedFavHomes }
  ) {
    commit('SET_RHT', { ...rootState.rhtValue, data: updatedRhtHomes })
    commit('SET_PROPERTIES_DATA', {
      ...rootState.propertiesData,
      propertyData: updatedFavHomes
    })
    commit('client/SAVE_CLIENT_FAVORITES', updatedFavHomes, { root: true })
  },

  agentRouteDataAction({ commit, dispatch, rootState }, data) {
    let routeArray = []
    data.clients.map(client => {
      if (!isEmpty(client.routes)) {
        client.routes.forEach(route => {
          return routeArray.push({
            ...route,
            clientId: client._id,
            clientName: client.name,
            ...createCalendarTimeFrame(route)
          })
        })
      }
    })
    commit(
      'SET_AGENT_ROUTE_DATA',
      sortObjectArrayByKey(routeArray, 'startTime', 'desc')
    )
    commit('common/SET_LOADING_ROUTES', false, { root: true })
    if (!rootState.common.isMobile) {
      dispatch('agentRouteDataActionInterval')
    }
  },

  async agentRouteDataActionInterval({ commit, rootState, state }) {
    if (interval) return
    interval = setInterval(async () => {
      if (!isEmpty(rootState.client.clientData)) {
        let activeClient = rootState.client.clientData
        const { updatedData, updated } = await setProfileLastUpdatedTimestamp(
          activeClient
        )

        if (updated) {
          // do client/agent profile update here
          // this will be handling other updates too like RHT list, feedback etc.
          // so possibly this is a temporary location, will be looked into later
          const response = await genericGetRequestToApi(RetrieveProfileUrl())
          const activeClientData = response.data.clients.find(
            client => client._id === updatedData._id
          )

          const updatedClientData = {
            ...updatedData,
            ...activeClientData,
            routes: [...activeClientData.routes]
          }

          const updatedAgentClients = state.agentData.clients.map(client =>
            client._id === updatedClientData._id ? updatedClientData : client
          )

          const otherClientRoutes = state.agentRouteData.filter(
            route => route.clientId !== updatedData._id
          )

          updatedClientData.routes.map(route => {
            const updatedRoute = {
              ...route,
              clientId: activeClient._id,
              clientName: activeClient.name,
              ...createCalendarTimeFrame(route)
            }
            otherClientRoutes.push(updatedRoute)
            return updatedRoute
          })
          commit('client/SET_CLIENT_DATA', updatedClientData, { root: true })
          commit('SET_AGENT_DATA', {
            ...state.agentData,
            clients: updatedAgentClients
          })
          commit('SET_RESET_EXPANSION_PANELS', true)
          commit(
            'SET_AGENT_ROUTE_DATA',
            sortObjectArrayByKey(otherClientRoutes, 'startTime', 'desc')
          )
        }
      }
    }, 7000)
  },

  async fetchAgentMessagesAction({ commit, dispatch, state }) {
    const res = await getAllMessages()
    commit('SET_AGENT_MESSAGES', res)
    dispatch('getDialogsUsers')
    dispatch('fetchAgentMessagesActionInterval')
  },

  async fetchAgentMessagesActionInterval({ commit, dispatch, state }) {
    if (interval) return
    interval = setInterval(async () => {
      const res = await getAllMessages()
      commit('SET_AGENT_MESSAGES', res)
      dispatch('getDialogsUsers')
      if (state.selectedDialogUser) {
        const messagesWithUser = res
          .filter(
            message =>
              message.from === state.selectedDialogUser._id ||
              message.to[0] === state.selectedDialogUser._id
          )
          .sort((a, b) => new Date(a._timestamp) - new Date(b._timestamp))
        const threads = calculateThreadsFromMessages(
          messagesWithUser,
          state.agentData
        )

        dispatch('communication/setThreads', threads, { root: true })
      }
    }, 10000)
  },

  setDialogUser({ commit, state, dispatch }, user) {
    if (state.agentMessages) {
      commit('SET_DIALOG_USER', user)
      if (user) {
        const messagesWithUser = state.agentMessages
          .filter(
            message =>
              message.from === state.selectedDialogUser._id ||
              message.to[0] === state.selectedDialogUser._id
          )
          .sort((a, b) => new Date(a._timestamp) - new Date(b._timestamp))
        const threads = calculateThreadsFromMessages(messagesWithUser)
        dispatch('communication/setThreads', threads, { root: true })
      } else {
        dispatch('communication/setThreads', [], { root: true })
      }
    }
  },

  async markMessagesAsRead({ commit }, data) {
    await Promise.all(
      data.messages.map(message => markMessageAsRead(message._id))
    )
    commit('MARK_AGENT_MESSAGE_AS_READ', data.user)
  },

  setSelectedTab({ commit }, selectedTab) {
    commit('SET_SELECTED_TAB', selectedTab)
  }
}

const mutations = {
  SET_RESET_EXPANSION_PANELS: (state, status) => {
    state.resetExpansionPanels = status
  },

  SET_HIDE_MAP: (state, status) => {
    state.hideMap = status
  },

  SET_RHT: (state, rhtValue) => {
    state.rhtValue = { ...rhtValue }
  },

  SET_AGENT_DATA: (state, payload) => {
    state.agentData = payload
  },

  SET_DIALOG_USER: (state, payload = null) => {
    state.selectedDialogUser = payload
  },

  SET_AGENT_MESSAGES: (state, payload) => {
    state.agentMessages = payload
  },

  SET_AGENT_ROUTE_DATA: (state, routeData) => {
    state.agentRouteData = routeData
  },

  SET_ROUTE_EDIT_MODE: (state, mode) => {
    state.routeEditMode = mode
  },

  SET_ROUTE_CALCULATED_ARRAY: (state, array) => {
    state.routeCalculatedArray = array
  },

  SET_FAVORABLE_VIEW_TIME: (state, timeMins) => {
    state.favorableViewTime = timeMins
  },

  // Storing all properties data at one place when loaded
  SET_PROPERTIES_DATA: (state, payload) => {
    if (
      !state.propertiesData.some(data => data.clientId === payload.clientId)
    ) {
      state.propertiesData.push(payload)
    }
  },

  UPDATE_PROPERTIES_DATA: (state, payload) => {
    const currentProperties = state.propertiesData.find(
      data => data.clientId === payload.clientId
    )
    if (!isEmpty(currentProperties)) {
      state.propertiesData[payload.clientId] = payload.propertyData
    } else {
      state.propertiesData.push(payload)
    }
  },

  SET_SELECTED_TAB: (state, payload) => {
    state.selectedTab = payload
  },

  CLEAR_CLIENT_SAVED_HOMES: state => {
    if (state.clientSavedHomes) {
      state.clientSavedHomes = []
    }
  },

  SET_CLIENT_SAVED_HOMES: (state, homeDetails) => {
    state.clientSavedHomes.push(homeDetails)
  },

  SET_SELECTED_START_LOCATION: (state, payload) => {
    state.selectedStartLocation = payload
  },

  SET_SELECTED_END_LOCATION: (state, location) => {
    state.selectedEndLocation = location
  },

  SET_DIALOGS: (state, payload) => {
    state.agentDialogs = payload
  },

  MARK_AGENT_MESSAGE_AS_READ: (state, selectedUser) => {
    state.agentDialogs = state.agentDialogs.map(user => {
      if (user._id === selectedUser._id) {
        user.unreadMessages.forEach(message => {
          message.read = true
        })
        user.unreadMessages = []
      }
      return user
    })
  },

  SET_DEFAULT_AGENT_STATE: state => {
    Object.assign(state, getDefaultAgentState())
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
